import type { DragEndEvent } from "@dnd-kit/core";
import { Add as AddIcon } from "@mui/icons-material";
import { Box, Button, Checkbox, FormControlLabel, TextField, Typography } from "@mui/material";
import { type Control, Controller, useFieldArray, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { LessonFormData } from "api/services/content/schemas/lessons";
import { SortableList } from "shared/atoms/DragAndDrop";

import { SortableItem } from "./SortableItem";

interface Props {
  partIndex: number;
  questionIndex: number;
  control: Control<LessonFormData>;
}

export const AnswersSection: React.FC<Props> = ({ partIndex, questionIndex, control }) => {
  const { t } = useTranslation();
  const { errors } = useFormState({ control });
  const { fields, append, move, remove } = useFieldArray({
    control,
    name: `parts.${partIndex}.translation.questions.${questionIndex}.answers`,
  });

  const addAnswer = () => {
    append({ text: "", correct: false });
  };

  const removeAnswer = (index: number) => {
    remove(index);
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = fields.findIndex(field => field.id === active.id);
      const newIndex = fields.findIndex(field => field.id === over.id);
      move(oldIndex, newIndex);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography variant="subtitle2">{t("library.lessons.fields.answers")}</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addAnswer} sx={{ ml: 2 }}>
          {t("buttons.add_answer")}
        </Button>
      </Box>

      <SortableList items={fields.map(f => f.id)} onDragEnd={onDragEnd}>
        {fields.map((fieldItem, index) => (
          <SortableItem
            key={fieldItem.id}
            fieldItem={fieldItem}
            index={index}
            hasMoreThanOne={fields.length > 1}
            label={`${t("library.lessons.fields.question")} ${index + 1}`}
            onRemove={() => removeAnswer(index)}
          >
            <Controller
              name={`parts.${partIndex}.translation.questions.${questionIndex}.answers.${index}.text`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={`${t("library.lessons.fields.answer")} ${index + 1}`}
                  fullWidth
                  size="small"
                  error={!!errors.parts?.[partIndex]?.translation?.questions?.[questionIndex]?.answers?.[index]?.text}
                  helperText={
                    !!errors.parts?.[partIndex]?.translation?.questions?.[questionIndex]?.answers?.[index]?.text &&
                    t("errors.field.required")
                  }
                />
              )}
            />

            <Controller
              name={`parts.${partIndex}.translation.questions.${questionIndex}.answers.${index}.correct`}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} sx={{ ml: 1 }} />}
                  label={t("library.lessons.fields.answer_correct")}
                />
              )}
            />
          </SortableItem>
        ))}
      </SortableList>
    </Box>
  );
};
